import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthLayout, ErrorAlert } from "../components/AuthLayout";
import { PasswordInput } from "../components/AuthFields";
import { FullPageLoader } from "../components/RouteGuards";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { homeForRole } from "../lib/authApi";
import { useNoindex } from "../hooks/useNoindex";

export default function ResetPassword() {
  useNoindex();
  const { session, role, loading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (loading) return <FullPageLoader />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (err) return setError(err.message);
    navigate(homeForRole(role), { replace: true });
  };

  return (
    <AuthLayout testId="reset-password-page" eyebrow="Account help" title="Set a new password">
      {!session ? (
        <div className="space-y-4">
          <ErrorAlert message="This reset link is invalid or has expired. Please request a new one." />
          <Link to="/forgot-password" data-testid="request-new-link" className="block text-center text-sm font-semibold text-[#7A0016] hover:underline">Request a new link</Link>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-4">
          <PasswordInput id="new-password" label="New password" value={password} onChange={(e) => setPassword(e.target.value)} testId="new-password-input" />
          <PasswordInput id="confirm-password" label="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} testId="confirm-new-password-input" />
          <ErrorAlert message={error} />
          <Button type="submit" disabled={busy} data-testid="update-password-button" className="h-11 w-full rounded-full bg-[#7A0016] font-semibold text-[#F5E6BA] hover:bg-[#4A000E]">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
